/**
 * Hero dock main screen register form (`main_screen_variant: hero_dock`).
 * Cloned from classic — safe to tune independently for hero_dock.
 */
import React, { useState, useCallback, useMemo, useRef } from 'react';
import { View, Text, Alert, ScrollView, Platform, Keyboard } from 'react-native';
import { SvgIcon } from '../../../components/common/SvgIcon';
import { Clickable } from '../../../components/common/Clickable';
import { ButtonLoader } from '../../../components/load-main/ButtonLoader';
import { Input } from '../../../components/common/Input';
import { AppPickerTrigger } from '../../../components/common/AppPickerTrigger';
import { AreaPickerModal } from '../../../components/common/AreaPickerModal';
import { appTheme, useThemeColors } from '../../../shared/theme';
import { postRegisterRequest, registerSuccessPersistToStore, enrichSignupOtpParams } from '../../../shared/auth';
import { getFcmDeviceTokenForAuth } from '../../../platform/notifications/fcmDeviceToken';
import { useAreas } from '../../../data/domains/addresses';
import { useOrganizationSettings, resolvePhoneMaxDigits } from '../../../data/domains/config';
import { useTranslation } from '../../../shared/i18n';
import { setSelectedSubscription } from '../../../data/domains/subscription';
import { RegisterTermsModal } from '../../RegisterTermsModal';
import {
  sharedStyles,
  formFieldHalf,
  formFieldRow,
  createSolidPrimaryCtaLabelStyle,
} from '../../../shared/theme/sharedStyles';
import { createCredentialFormSheetStyles, createCredentialPrimaryCtaSurfaceStyle, credentialFormPrimaryButtonWrap, createRegistrationFormSheetStyles } from '../../../shared/theme/authSheetStyles';
import { useAuthSheets } from '../../../shared/auth/authSheetContext';
import { authSheetFormSpacing, AUTH_FORM_KEYBOARD } from '../../../shared/auth';
import { useAuthInputKeyboardAvoidance } from '../../keyboard/useAuthInputKeyboardAvoidance';
import { AuthFocusTarget } from '../../keyboard/AuthFocusTarget';
import { androidMainScreenAuthFormScrollSurface } from '../../../shared/auth/mainScreenAndroidFormSurface';
import { androidMainScreenIntrinsicFormRootStyle, androidMainScreenIntrinsicScrollCapStyle, isMainScreenAndroid } from '../../../shared/auth/onboardingMainScreenAndroidParity';
import { isHasLastNameEnabled } from '../../../data/domains/config';

export type RegisterSheetContentProps = {
  onDismiss: () => void;
  /** Main onboarding float: switch to login without `openLogin` (Reanimated-only path). */
  onInlineSwitchToLogin?: () => void;
  subData?: unknown;
  mainScreenIntrinsic?: boolean;
  mainScreenScrollMaxHeight?: number;
  onFormLayoutHeight?: (heightPx: number) => void;
  /** MainScreen only: extra panel lift when scroll cannot clear the focused field. */
  onMainScreenKeyboardNudge?: (offset_px: number) => void;
};

export function RegisterSheetContent({
  onDismiss: _onDismiss,
  onInlineSwitchToLogin,
  subData,
  mainScreenIntrinsic = false,
  mainScreenScrollMaxHeight,
  onFormLayoutHeight,
  onMainScreenKeyboardNudge,
}: RegisterSheetContentProps) {
  const { t } = useTranslation();
  const colors = useThemeColors() ?? appTheme.colors;
  const layout = useMemo(() => createCredentialFormSheetStyles(colors), [colors]);
  const styles = useMemo(() => createRegistrationFormSheetStyles(colors), [colors]);
  const { openLogin, openOtp } = useAuthSheets();
  const {
    settings: { allowedDigitForPhone: userPhoneAllowedDigit, dialing_code: dialing_code, showReferralField: showRef },
  } = useOrganizationSettings();
  const has_last_name_enabled = isHasLastNameEnabled();

  const display_dialing_code = dialing_code != null ? String(dialing_code) : '965';
  const phone_max_digits = resolvePhoneMaxDigits(userPhoneAllowedDigit, display_dialing_code);

  const [formData, setFormData] = useState({
    name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    areasOrRegionId: '',
    governorate_id: '',
    referral_code: '',
    dialing_code: null,
    device_type: Platform.OS,
    device_token: '',
  });

  const [tcChecked, setTcChecked] = useState(false);
  const [terms_visible, setTermsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAreaName, setSelectedAreaName] = useState('');
  const [showAreaPicker, setShowAreaPicker] = useState(false);
  const [errors, setErrors] = useState<Record<string, boolean>>({});

  const openTermsScreen = useCallback(() => {
    setTermsVisible(true);
  }, []);

  const handleTermsAccept = useCallback(() => {
    setTcChecked(true);
    setErrors(prev => ({ ...prev, tc: false }));
  }, []);

  const toggleTermsChecked = useCallback(() => {
    setTcChecked(prev => !prev);
    setErrors(prev => ({ ...prev, tc: false }));
  }, []);

  const { data: areas, isLoading: areasLoading, error: areasError, refetch: refetchAreas } = useAreas();

  const updateField = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: false }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, boolean> = {};
    if (!formData.name || formData.name.trim().length < 2) {
      newErrors.name = true;
    }
    if (has_last_name_enabled && (!formData.last_name || formData.last_name.trim().length < 2)) {
      newErrors.last_name = true;
    }
    if (!formData.email || formData.email.trim().length < 5) {
      newErrors.mail = true;
    }
    if (!formData.phone || formData.phone.trim().length < 6) {
      newErrors.phone = true;
    }
    if (!formData.password || formData.password.trim().length < 6) {
      newErrors.password = true;
    }
    if (!formData.areasOrRegionId) {
      newErrors.area = true;
    }
    if (!tcChecked) {
      newErrors.tc = true;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRegister = async () => {
    if (!validateForm()) {
      Alert.alert(t('common.error'), t('auth.fillAllFields'));
      return;
    }

    setIsLoading(true);

    try {
      const device_token = await getFcmDeviceTokenForAuth();
      const payload = {
        ...formData,
        last_name: has_last_name_enabled ? formData.last_name : formData.name,
        device_token,
        TcChecked: tcChecked,
      };
      const response = (await postRegisterRequest(payload)) as {
        success?: boolean;
        message?: string;
        data?: { customer?: unknown };
        customer?: unknown;
      };
      if (response.success !== true) {
        Alert.alert(t('common.error'), response.message || t('auth.registrationFailed'));
        return;
      }
      const has_customer = Boolean(
        (response.data && (response.data as { customer?: unknown }).customer) ?? (response as { customer?: unknown }).customer
      );
      if (has_customer) {
        const persist = await registerSuccessPersistToStore(response);
        if (!persist.ok) {
          Alert.alert(t('common.error'), persist.message || t('auth.registrationFailed'));
          return;
        }
      }

      /** Guest plan flow: address first, then checkout with plan from `selectedSubscriptionStorage`. */
      const redirectRoute = subData ? 'AddressForm' : 'SubscriptionList';
      const navigationPayload: Record<string, unknown> = {
        phone: formData.phone,
        countryCode: dialing_code != null ? `+${dialing_code}` : '+965',
        context: 'signup',
        redirectTo: redirectRoute,
        signupPassword: formData.password,
      };

      if (subData) {
        navigationPayload.checkoutPayload = subData;
        await setSelectedSubscription(subData as object);
      }

      openOtp(enrichSignupOtpParams(navigationPayload));
    } catch (error: any) {
      if (error.status === 422) {
        Alert.alert(t('common.error'), t('auth.fillAllFieldsCorrectly'));
      } else {
        Alert.alert(t('common.error'), error.message || t('auth.unexpectedError'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const selectArea = (area: any) => {
    Keyboard.dismiss();
    updateField('areasOrRegionId', String(area.id));
    updateField('governorate_id', area.governorate_id != null && area.governorate_id !== '' ? String(area.governorate_id) : '');
    setSelectedAreaName(area.title ?? area.name ?? '');
    setErrors(prev => ({ ...prev, area: false }));
    setShowAreaPicker(false);
  };

  const scroll_ref = useRef<ScrollView>(null);
  const scroll_y = useRef(0);
  const name_row_ref = useRef<View>(null);
  const email_block_ref = useRef<View>(null);
  const phone_block_ref = useRef<View>(null);
  const password_block_ref = useRef<View>(null);
  const area_block_ref = useRef<View>(null);
  const referral_block_ref = useRef<View>(null);
  const { on_field_focus, on_scroll_layout, on_scroll_content_size_change, keyboard_scroll_pad } = useAuthInputKeyboardAvoidance({
    enabled: true,
    scroll_ref,
    scroll_y_ref: scroll_y,
    on_panel_offset: onMainScreenKeyboardNudge,
    clearance_px: AUTH_FORM_KEYBOARD.fieldFocusClearancePx,
  });

  const auth_primary_cta = useMemo(
    () => ({
      surface: createCredentialPrimaryCtaSurfaceStyle(colors),
      label: createSolidPrimaryCtaLabelStyle(colors),
    }),
    [colors],
  );

  const intrinsic_keyboard =
    mainScreenIntrinsic && isMainScreenAndroid()
      ? androidMainScreenIntrinsicFormRootStyle(mainScreenScrollMaxHeight)
      : mainScreenIntrinsic
        ? Platform.OS === 'web'
          ? { width: '100%' as const, alignSelf: 'stretch' as const, flex: 1, minHeight: 0, height: '100%' as const }
          : { width: '100%' as const, alignSelf: 'stretch' as const, flex: 0, minHeight: 0 }
        : null;
  const intrinsic_scroll =
    mainScreenIntrinsic
      ? Platform.OS === 'web'
        ? { flex: 1, width: '100%' as const, height: '100%' as const }
        : { flex: 0, flexGrow: 0, flexShrink: 1 }
      : null;
  const intrinsic_scroll_content = mainScreenIntrinsic ? { flexGrow: 0 } : null;
  const intrinsic_scroll_cap =
    mainScreenIntrinsic && mainScreenScrollMaxHeight != null
      ? isMainScreenAndroid()
        ? androidMainScreenIntrinsicScrollCapStyle(mainScreenScrollMaxHeight)
        : Platform.OS === 'web'
          ? {
              maxHeight: mainScreenScrollMaxHeight,
              width: '100%' as const,
              flex: 1,
              height: '100%' as const,
              minHeight: 0,
            }
          : { maxHeight: mainScreenScrollMaxHeight, width: '100%' as const, flexShrink: 1 }
      : null;

  const base_scroll_pad_bottom = mainScreenIntrinsic ? appTheme.spacing.sm : authSheetFormSpacing.formPaddingBottom;

  const register_scroll_body = (
    <ScrollView
      ref={scroll_ref}
      onScroll={e => {
        scroll_y.current = e.nativeEvent.contentOffset.y;
      }}
      onLayout={on_scroll_layout}
      onContentSizeChange={(w, h) => {
        on_scroll_content_size_change(w, h);
        if (mainScreenIntrinsic && onFormLayoutHeight) {
          onFormLayoutHeight(h);
        }
      }}
      scrollEventThrottle={16}
      style={[layout.scroll, intrinsic_scroll]}
      contentContainerStyle={[
        layout.scrollContent,
        mainScreenIntrinsic ? layout.mainScreenScrollContent : null,
        mainScreenIntrinsic ? androidMainScreenAuthFormScrollSurface(colors) : null,
        intrinsic_scroll_content,
        mainScreenIntrinsic
          ? { paddingBottom: base_scroll_pad_bottom + keyboard_scroll_pad }
          : { paddingTop: appTheme.spacing.md, paddingBottom: base_scroll_pad_bottom + keyboard_scroll_pad },
      ]}
      showsVerticalScrollIndicator={mainScreenIntrinsic}
      nestedScrollEnabled={Platform.OS === 'android' && mainScreenIntrinsic}
      keyboardShouldPersistTaps="handled"
      keyboardDismissMode="on-drag"
      automaticallyAdjustKeyboardInsets={false}
      bounces={false}>
      <View style={mainScreenIntrinsic ? layout.mainScreenFields : layout.fields}>
        <AuthFocusTarget ref={name_row_ref} style={has_last_name_enabled ? formFieldRow : undefined}>
          <Input
            value={formData.name}
            onChangeText={v => updateField('name', v)}
            onFocus={() => on_field_focus(name_row_ref)}
            placeholder={t('auth.enterFirstName')}
            error={errors.name ? t('auth.fillAllFields') : undefined}
            containerStyle={[sharedStyles.inputContainerFlush, has_last_name_enabled ? formFieldHalf : undefined]}
          />
          {has_last_name_enabled ? (
            <Input
              value={formData.last_name}
              onChangeText={v => updateField('last_name', v)}
              onFocus={() => on_field_focus(name_row_ref)}
              placeholder={t('auth.enterLastName') || 'Enter last name'}
              error={errors.last_name ? t('auth.fillAllFields') : undefined}
              containerStyle={[sharedStyles.inputContainerFlush, formFieldHalf]}
            />
          ) : null}
        </AuthFocusTarget>
        <AuthFocusTarget ref={email_block_ref}>
          <Input
            value={formData.email}
            onChangeText={v => updateField('email', v)}
            onFocus={() => on_field_focus(email_block_ref)}
            placeholder={t('auth.enterEmail')}
            keyboardType="email-address"
            autoCapitalize="none"
            error={errors.email ? t('auth.fillAllFields') : undefined}
            containerStyle={sharedStyles.inputContainerFlush}
          />
        </AuthFocusTarget>
        <AuthFocusTarget ref={phone_block_ref}>
          <Input
            label={t('auth.phoneNumber')}
            value={formData.phone}
            onChangeText={v => updateField('phone', v.replace(/\D/g, '').slice(0, phone_max_digits))}
            onFocus={() => on_field_focus(phone_block_ref)}
            placeholder={t('auth.phonePlaceholder')}
            keyboardType="phone-pad"
            maxLength={phone_max_digits}
            error={errors.phone ? t('auth.fillAllFields') : undefined}
            containerStyle={sharedStyles.inputContainerFlush}
            leftAccessory={<Text style={styles.countryCodeText}>+{display_dialing_code}</Text>}
          />
        </AuthFocusTarget>
        <AuthFocusTarget ref={password_block_ref}>
          <Input
            value={formData.password}
            onChangeText={v => updateField('password', v)}
            onFocus={() => on_field_focus(password_block_ref)}
            placeholder={t('auth.enterPassword') || 'Enter password'}
            secureTextEntry
            error={errors.password ? t('auth.fillAllFields') : undefined}
            containerStyle={sharedStyles.inputContainerFlush}
          />
        </AuthFocusTarget>
        <AuthFocusTarget ref={area_block_ref}>
          <AppPickerTrigger
            value={selectedAreaName || null}
            placeholder={areasLoading ? (t('common.loading') ?? 'Loading...') : areasError && (areas?.length ?? 0) === 0 ? (t('common.retry') ?? 'Tap to retry') : t('auth.selectArea')}
            onPress={() => (areasError && (areas?.length ?? 0) === 0 ? refetchAreas() : setShowAreaPicker(true))}
            disabled={areasLoading}
            loading={areasLoading}
            error={errors.area ? t('auth.required') : undefined}
            accessibilityLabel={t('auth.selectArea')}
            containerStyle={sharedStyles.inputContainerFlush}
          />
        </AuthFocusTarget>
        {showRef ? (
          <AuthFocusTarget ref={referral_block_ref}>
            <Input
              value={formData.referral_code}
              onChangeText={v => updateField('referral_code', v)}
              onFocus={() => on_field_focus(referral_block_ref)}
              placeholder={t('auth.enterReferralCode')}
              autoCapitalize="characters"
              containerStyle={sharedStyles.inputContainerFlush}
            />
          </AuthFocusTarget>
        ) : null}
      </View>

      <View style={mainScreenIntrinsic ? styles.tcRowMainScreen : styles.tcRow}>
        <Clickable
          useNativePressable
          testID="create-account-terms-checkbox"
          onPress={toggleTermsChecked}
          accessibilityRole="checkbox"
          accessibilityState={{ checked: tcChecked }}
          accessibilityLabel={t('auth.agreeToTerms')}
          hitSlop={appTheme.hitSlop.small}>
          <View style={[styles.checkbox, tcChecked && styles.checkboxChecked, errors.tc && styles.checkboxInvalid]}>
            {tcChecked ? <SvgIcon name="check" size={appTheme.sizes.iconSizeSm} color={colors.onPrimary} /> : null}
          </View>
        </Clickable>
        <Clickable
          useNativePressable
          testID="create-account-terms-link"
          onPress={openTermsScreen}
          colorVariant="link"
          accessibilityRole="link"
          accessibilityLabel={`${t('auth.agreeToTerms')}${t('auth.termsAndConditions')}`}
          hitSlop={appTheme.hitSlop.small}
          style={styles.tcTextBlock}>
          <Text style={styles.tcText} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.85}>
            {t('auth.agreeToTerms')}
            <Text style={styles.tcLink}>{t('auth.termsAndConditions')}</Text>
          </Text>
        </Clickable>
      </View>

      <View style={sharedStyles.buttonStack}>
        <Clickable
          useNativePressable
          testID="create-account-continue"
          onPress={handleRegister}
          disabled={isLoading}
          colorVariant="primary"
          haptics
          hapticType="impactLight"
          contentBorderRadius={appTheme.borderRadius.lg}
          style={{ alignSelf: 'stretch', flexShrink: 0 }}
          accessibilityLabel={t('auth.createAccount')}
          accessibilityRole="button"
          accessibilityState={{ disabled: isLoading }}
        >
          <View style={credentialFormPrimaryButtonWrap(colors)}>
            <View style={auth_primary_cta.surface}>
              {isLoading ? <ButtonLoader size={16} color={colors.onPrimary ?? colors.primaryText ?? colors.white} /> : <Text style={auth_primary_cta.label}>{t('auth.createAccount')}</Text>}
            </View>
          </View>
        </Clickable>
      </View>

      <View style={{ marginTop: mainScreenIntrinsic ? authSheetFormSpacing.afterPrimaryActionsMainScreen : authSheetFormSpacing.afterPrimaryActions }}>
        <View style={styles.loginRow}>
          <Text style={styles.loginText}>{t('auth.alreadyHaveAccount')}</Text>
          <Clickable
            useNativePressable
            testID="create-account-signin"
            onPress={onInlineSwitchToLogin ?? openLogin}
            colorVariant="link"
            accessibilityRole="link"
            accessibilityLabel={t('auth.signIn')}
            hitSlop={appTheme.hitSlop.small}
          >
            <Text style={styles.loginLink}>{t('auth.signIn')}</Text>
          </Clickable>
        </View>
      </View>
    </ScrollView>
  );

  return (
    <>
      <View style={[layout.keyboardView, intrinsic_keyboard]}>{intrinsic_scroll_cap != null ? <View style={intrinsic_scroll_cap}>{register_scroll_body}</View> : register_scroll_body}</View>

      <AreaPickerModal visible={showAreaPicker} areas={areas ?? []} onSelect={selectArea} onClose={() => setShowAreaPicker(false)} selectedAreaId={formData.areasOrRegionId} />
      <RegisterTermsModal visible={terms_visible} onClose={() => setTermsVisible(false)} onAccept={handleTermsAccept} />
    </>
  );
}

export default RegisterSheetContent;
